import { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { ItemsAPI } from '../../api/payments/items/api';
import { rowClickHandler } from '../../components/jds/rowClick';
import { buildItemsQueryParams, extractItems } from './utils';

const SEARCH_DEBOUNCE_MS = 400;

export function useItemsBootstrap() {
  const history = useHistory();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState('');
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  // Só a requisição mais recente pode atualizar o estado (descarta respostas antigas).
  const requestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);

  // Busca e paginação no servidor (jussub): page é 0-based lá, 1-based aqui.
  const fetchItems = useCallback(
    async (page, customPerPage = perPage) => {
      const requestId = ++requestIdRef.current;
      const isStale = () => requestId !== requestIdRef.current;

      setLoading(true);
      setErrorMessage('');

      try {
        const params = buildItemsQueryParams({
          page: page - 1,
          limit: customPerPage,
          filterText,
        });

        const response = await ItemsAPI.list(params);

        if (isStale()) return;

        if (response.data?.success) {
          setData(extractItems(response));
          setTotalRows(
            response.data.data?.totalElements ??
              response.data.totalElements ??
              0,
          );
        } else {
          setData([]);
          setTotalRows(0);
        }
      } catch (error) {
        if (isStale()) return;

        setErrorMessage(
          'Nao foi possivel carregar os produtos. Tente novamente.',
        );

        setData([]);
        setTotalRows(0);

        console.error('Erro ao buscar itens:', error);
      } finally {
        if (!isStale()) setLoading(false);
      }
    },
    [perPage, filterText],
  );

  const handlePageChange = (page) => {
    // Troca explícita de página cancela o debounce pendente (evita fetchItems(1) sobrescrever).
    clearTimeout(debounceTimerRef.current);
    setCurrentPage(page);
    fetchItems(page);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    setResetPaginationToggle((previous) => !previous);
    fetchItems(1, newPerPage);
  };

  const handleFilterChange = (event) => {
    setFilterText(event.target.value);
  };

  useEffect(() => {
    setCurrentPage(1);
    const timeoutId = setTimeout(() => fetchItems(1), SEARCH_DEBOUNCE_MS);
    debounceTimerRef.current = timeoutId;
    return () => {
      clearTimeout(timeoutId);
      // Invalida requisição em voo durante o debounce e no unmount.
      requestIdRef.current++;
    };
  }, [filterText, fetchItems]);

  const handleRowClicked = rowClickHandler(history, (item) => ({
    pathname: `/payments/items/${item.id}`,
  }));

  return {
    data,
    loading,
    errorMessage,
    totalRows,
    perPage,
    currentPage,
    filterText,
    resetPaginationToggle,
    handlePageChange,
    handlePerRowsChange,
    handleFilterChange,
    handleRowClicked,
  };
}
